import { useCallback, useRef } from 'react';
import { useNavigate, useMatch, useSearchParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { GET_WORKLIST_ITEM, GET_LAST_VIEWED_WORKLIST_ITEM_SMIDS } from 'modules/Apollo/queries';
import type {
  GetWorklistItemQuery,
  GetWorklistItemQueryVariables,
  GetLastViewedWorklistItemSmidsQuery,
  GetLastViewedWorklistItemSmidsQueryVariables,
} from 'generated/graphql';
import { AppRoutePath, NOT_FOUND_CASE_ID } from 'config/constants';
import { useAsyncGlobalAction } from 'hooks/useGlobalAction';
import { useOpenTabs } from './useOpenTabs';
import { FF, useFeatureFlagEnabled } from 'modules/feature-flags';
import usePrevious from 'react-use/esm/usePrevious';
import { useUrlBuilder } from './useUrlBuilder';
import { PRIOR_STUDIES_PARAM, EXCLUDED_AUTOLOADED_PRIOR_STUDIES_PARAM } from './studyParams';
import { clearProviderMap } from 'modules/viewer/workers/PixelDataSharedWorker';
import { saveWindowSyncInfo } from 'modules/windowDesyncDetector';
import { logger } from 'modules/logger';
import { useStudies } from '../domains/viewer/Viewer/StudyLoader/useStudies';
import { getWARDCaseId } from 'domains/viewer/Viewer/Ward/utils';
import { WithApolloContext } from 'modules/Apollo/types';
import { useViewerMatch } from 'utils/viewerUrlUtils';

type HookResult = {
  currentCaseId: Nullable<string>;
  /** Current case/worklist item in context */
  currentCase: Nullable<GetWorklistItemQuery['worklistItem']>;
  /** Refetches the case globally across all windows */
  refreshCase: () => Promise<void>;
  /** Loading state for case */
  loadingCase: boolean;
};

export const useCurrentComparativeStudies = ({
  context,
}: WithApolloContext): [
  ReadonlyArray<string>,
  (arg1: { caseSmid?: string; studySmids: ReadonlyArray<string> }) => unknown,
] => {
  const currentCaseId = useCurrentCaseId();
  const { priorStudiesIds, autoloadedStudiesIds } = useStudies({ context });

  const buildUrl = useUrlBuilder();
  const navigate = useNavigate();
  const previousCaseId = usePrevious(currentCaseId);
  const [shouldRefreshViewer] = useFeatureFlagEnabled(FF.refresh_viewer_on_new_case);
  const matchViewer = useMatch(AppRoutePath.VIEWER);
  const [searchParams] = useSearchParams();

  const setCurrentComparativeStudies = useCallback(
    ({
      caseSmid = currentCaseId,
      studySmids,
    }: {
      caseSmid?: Nullable<string>;
      studySmids: ReadonlyArray<string>;
    }) => {
      if (caseSmid == null) return;
      const builtUrl = buildUrl({
        caseSmid,
        studySmids,
        autoloadedStudySmids: autoloadedStudiesIds,
      });

      if (builtUrl == null) return;

      const isSameCase = previousCaseId === caseSmid;

      if (isSameCase) {
        // Preserve unrelated query params (e.g. `hp=create` for the Hanging Protocol form)
        // that the URL builder doesn't manage, so navigation doesn't reset widgets bound to them.
        const preservedParams = new URLSearchParams(searchParams);
        preservedParams.delete(PRIOR_STUDIES_PARAM);
        preservedParams.delete(EXCLUDED_AUTOLOADED_PRIOR_STUDIES_PARAM);
        const preservedQueryString = preservedParams.toString();
        const separator = builtUrl.includes('?') ? '&' : '?';
        navigate(
          preservedQueryString.length > 0
            ? `${builtUrl}${separator}${preservedQueryString}`
            : builtUrl
        );
        return;
      }

      if (shouldRefreshViewer && matchViewer) {
        clearProviderMap();
        window.location.href = builtUrl;
      } else if (window.location.pathname !== builtUrl) {
        clearProviderMap();
        navigate(builtUrl);
      }
    },
    [
      currentCaseId,
      buildUrl,
      autoloadedStudiesIds,
      previousCaseId,
      navigate,
      shouldRefreshViewer,
      matchViewer,
      searchParams,
    ]
  );

  return [priorStudiesIds, setCurrentComparativeStudies];
};

export const useCurrentCaseId = (): Nullable<string> => {
  const [isClearPatientContextEnabled] = useFeatureFlagEnabled(FF.CLEAR_PATIENT_CONTEXT);

  // Highest priority as it has less chances of being stale
  const viewerUrlWorklistId = useMatch(AppRoutePath.VIEWER)?.params?.worklistId;
  const reporterUrlWorklistId = useMatch({ path: AppRoutePath.REPORTER, end: false })?.params
    ?.worklistId;
  const patientJacketUrlWorklistId = useMatch(AppRoutePath.PATIENT_JACKET)?.params?.smid;
  const patientJacketUndockedUrlWorklistId = useMatch(AppRoutePath.PATIENT_JACKET_UNDOCKED)?.params
    ?.smid;
  const wardWorklistId = getWARDCaseId();

  // Right after the url, as it may have a small delay and result stale
  const { tabs } = useOpenTabs();
  const worklistIds = tabs.map((tab) => tab.worklistIds).flat(1);
  const currentCaseID =
    viewerUrlWorklistId ??
    reporterUrlWorklistId ??
    patientJacketUrlWorklistId ??
    patientJacketUndockedUrlWorklistId ??
    wardWorklistId ??
    worklistIds[0];

  const { data } = useQuery<
    GetLastViewedWorklistItemSmidsQuery,
    GetLastViewedWorklistItemSmidsQueryVariables
  >(GET_LAST_VIEWED_WORKLIST_ITEM_SMIDS, {
    skip: currentCaseID != null || isClearPatientContextEnabled,
    context: { telemetry: { component: 'useCurrentCaseId' } },
  });

  if (isClearPatientContextEnabled) {
    return currentCaseID ?? NOT_FOUND_CASE_ID;
  }

  if (currentCaseID == null) {
    const lastViewedWorklistItemSmids = data?.me?.lastViewedWorklistItemSmids ?? [];
    return lastViewedWorklistItemSmids.length > 0 ? lastViewedWorklistItemSmids[0] : null;
  }

  return currentCaseID;
};

/** Builds and navigates to the URL with the caseId */
export const useSetCurrentCase = (): ((smid: string) => unknown) => {
  const navigate = useNavigate();
  const buildUrl = useUrlBuilder({ isActive: true });
  const [shouldRefreshViewer] = useFeatureFlagEnabled(FF.refresh_viewer_on_new_case);
  const matchViewer = useViewerMatch();

  return useCallback(
    (caseSmid: string) => {
      if (caseSmid == null || caseSmid === NOT_FOUND_CASE_ID) return;
      const url = buildUrl({ caseSmid });
      if (url == null) return;
      if (window.location.pathname !== url) {
        clearProviderMap();
        if (shouldRefreshViewer && matchViewer) {
          saveWindowSyncInfo(caseSmid);
          window.location.href = url;
        } else {
          navigate(url);
          saveWindowSyncInfo(caseSmid);
        }
      }
    },
    [buildUrl, matchViewer, navigate, shouldRefreshViewer]
  );
};

export const useCurrentCase = (): HookResult => {
  const currentCaseId = useCurrentCaseId();
  const {
    data: caseData,
    loading: loadingCase,
    refetch,
  } = useQuery<GetWorklistItemQuery, GetWorklistItemQueryVariables>(GET_WORKLIST_ITEM, {
    variables: currentCaseId == null ? undefined : { smid: currentCaseId },
    skip: currentCaseId == null || currentCaseId === NOT_FOUND_CASE_ID,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    context: {
      telemetry: {
        component: 'useCurrentCase',
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first',
      },
    },
  });

  const refetchWorklistItem = useCallback(async () => {
    if (currentCaseId != null) {
      logger.info('[Current Case]: refetching case');

      await refetch({ smid: currentCaseId });
    }
  }, [currentCaseId, refetch]);

  const prevData = useRef(caseData);
  if (caseData?.worklistItem?.smid !== prevData.current?.worklistItem?.smid) {
    prevData.current = caseData;
  }
  const currentCase = (caseData || prevData.current)?.worklistItem;

  const refreshCase = useAsyncGlobalAction('use-current-case', refetchWorklistItem);

  return {
    currentCaseId,
    currentCase,
    refreshCase,
    loadingCase: loadingCase && currentCase?.smid !== currentCaseId,
  };
};
